import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatThinkingIndicatorState } from './chat-thinking-indicator.state';

/** Hard-coded step thresholds in milliseconds: 0s → 3s → 5s → 7s */
const MESSAGE_THRESHOLDS = [0, 3000, 5000, 7000] as const;
const POLL_INTERVAL_MS = 500;

export class ChatThinkingIndicatorViewModel extends AbstractViewModel<ChatThinkingIndicatorState> {
  static initialState: ChatThinkingIndicatorState = {
    messageIndex: 0,
  };

  private startTime: number = Date.now();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private maxIndex = 0;

  constructor(messageCount: number) {
    super(ChatThinkingIndicatorViewModel.initialState);
    this.maxIndex = Math.min(messageCount, MESSAGE_THRESHOLDS.length) - 1;
  }

  /** Start the polling timer. Call once on mount. */
  start() {
    this.startTime = Date.now();

    this.intervalId = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      let index = 0;

      for (let i = MESSAGE_THRESHOLDS.length - 1; i >= 0; i--) {
        if (elapsed >= MESSAGE_THRESHOLDS[i]) {
          index = Math.min(i, this.maxIndex);
          break;
        }
      }

      const current = this.state.getValue().messageIndex;
      if (index !== current) {
        this.update((state) => {
          state.messageIndex = index;
        });
      }
    }, POLL_INTERVAL_MS);
  }

  /** Stop the polling timer. Call on unmount. */
  stop() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

export const useChatThinkingIndicatorViewModel = (messageCount: number) =>
  useViewModel<ChatThinkingIndicatorState, ChatThinkingIndicatorViewModel>(
    () => new ChatThinkingIndicatorViewModel(messageCount)
  );
